import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { putFetcher } from 'api/swr'
import Divider from 'components/ui/Divider'
import { cptThumb, layerIcon } from 'commons/cdn'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import useSWR, { mutate } from 'swr'

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {Array} props.components 要移动的组件列表
 * @param {() => void} [props.onSuccess]
 */
const ChangeLayerDialog = ({
  open,
  onOpenChange,
  components = [],
  onSuccess,
}) => {
  const { tid } = useParams()
  const { toast } = useToast()
  const { data: layers } = useSWR(open ? ['/layers', { tid }] : null)
  const [selectedLid, setSelectedLid] = useState(null)
  const [loading, setLoading] = useState(false)

  const isBatch = components.length > 1
  const primary = components[0]
  const currLayer = useMemo(
    () => layers?.find((layer) => layer.id === primary?.lid),
    [layers, primary?.lid],
  )

  useEffect(() => {
    if (open) {
      setSelectedLid(null)
      setLoading(false)
    }
  }, [open])

  const handleConfirm = async () => {
    if (!selectedLid || loading || components.length === 0) return
    setLoading(true)
    let success = 0
    let failed = 0
    try {
      for (const cpt of components) {
        try {
          await putFetcher(`/components/${cpt.id}`, {
            arg: { tid, key: 'lid', value: selectedLid },
          })
          success += 1
        } catch {
          failed += 1
        }
      }
      await mutate(['/components', { tid }])
      onSuccess?.()
      onOpenChange(false)
      toast({
        title: failed > 0 ? 'Layer update partially succeeded' : 'Layer updated',
        description:
          failed > 0
            ? `Moved ${success}, failed ${failed}`
            : `Moved ${success} component${success > 1 ? 's' : ''}`,
        className: failed > 0 ? undefined : 'bg-green-500 text-white',
        variant: failed > 0 ? 'destructive' : undefined,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (loading) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isBatch ? 'Move components to layer' : 'Move component to layer'}
          </DialogTitle>
          <DialogDescription>
            Select a target layer, then click Confirm to apply.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
          <div className="flex flex-wrap items-start gap-6">
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">
                {isBatch
                  ? `Selected components (${components.length})`
                  : 'Current component'}
              </div>
              <div className="flex flex-wrap gap-2">
                {(isBatch ? components.slice(0, 8) : components).map((cpt) => (
                  <div
                    key={cpt.id}
                    className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl border-2 bg-white"
                  >
                    <img
                      alt=""
                      src={cptThumb(cpt.cover ?? cpt.id)}
                      className="h-full w-full aspect-square object-contain rounded-xl"
                    />
                  </div>
                ))}
                {isBatch && components.length > 8 && (
                  <div className="flex h-24 w-24 items-center justify-center rounded-xl border-2 bg-muted text-sm text-muted-foreground">
                    +{components.length - 8}
                  </div>
                )}
              </div>
            </div>

            {!isBatch && currLayer && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">
                  Current layer
                </div>
                <div className="flex w-24 flex-col items-center gap-1.5">
                  <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl border-2 bg-white">
                    <img
                      src={layerIcon(currLayer.cover)}
                      alt=""
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <span className="w-full truncate text-center text-xs">
                    {currLayer.name}
                  </span>
                </div>
              </div>
            )}
          </div>

          <Divider />

          <div className="text-sm font-medium">Target layer</div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid grid-cols-4 gap-3 p-1 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7">
              {layers?.map((layer) => {
                const active = selectedLid === layer.id
                return (
                  <button
                    key={layer.id}
                    type="button"
                    disabled={loading}
                    onClick={() => setSelectedLid(layer.id)}
                    className={cn(
                      'flex flex-col items-center gap-1.5 rounded-xl border-2 bg-white p-2 transition-colors',
                      'hover:border-primary',
                      active && 'border-primary bg-primary/5',
                    )}
                  >
                    <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg bg-muted/30">
                      <img
                        src={layerIcon(layer.cover)}
                        alt=""
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <span className="w-full truncate text-center text-xs leading-tight">
                      {layer.name}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            disabled={loading}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={!selectedLid || loading}
            onClick={handleConfirm}
          >
            {loading ? 'Moving...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ChangeLayerDialog
